import { safeFrom, isSupabaseAvailable } from "@/lib/safe-supabase";
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError
} from "@/lib/service-errors";

// Types matching our UI needs
export type EventItem = {
  id: number;
  title: string;
  date: string; // ISO date string
  time: string; // e.g. "2:00 PM - 6:00 PM"
  location: string;
  category: string;
  description: string;
  capacity: number;
  registered: number;
  featured: boolean;
};

export type NewsArticle = {
  id: number;
  title: string;
  excerpt: string;
  date: string; // ISO date string
  category: string;
  author: string;
  image: string | null;
  featured: boolean;
};

export type Testimonial = {
  id: number;
  quote: string;
  author: string;
  role: string | null;
  membership: string | null; // e.g. "3 years"
};

export async function fetchEvents(): Promise<ServiceResponse<EventItem[]>> {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom("events")
      .select(
        "id, title, date, time, location, category, description, capacity, registered, featured"
      )
      .eq("published", true)
      .order("date", { ascending: true });

    if (error) {
      throw error;
    }

    return successResponse((data ?? []) as EventItem[]);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.fetchEvents'));
  }
}

export async function fetchFeaturedEvents(limit = 3): Promise<ServiceResponse<EventItem[]>> {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom("events")
      .select(
        "id, title, date, time, location, category, description, capacity, registered, featured"
      )
      .eq("published", true)
      .eq("featured", true)
      .order("date", { ascending: true })
      .limit(limit);

    if (error) {
      throw error;
    }

    return successResponse((data ?? []) as EventItem[]);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.fetchFeaturedEvents'));
  }
}

export async function fetchNewsArticles(): Promise<ServiceResponse<NewsArticle[]>> {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    // Align with existing schema: public.news
    const { data, error } = await safeFrom("news")
      .select("id, title, excerpt, created_at, featured_image_url, published")
      .eq("published", true)
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }

    // Map to UI shape while providing sensible fallbacks for fields not in schema
    const mapped: NewsArticle[] = (data ?? []).map((row: any) => ({
      id: row.id,
      title: row.title,
      excerpt: row.excerpt ?? "",
      date: row.created_at, // use created_at as date
      category: "",         // not in schema; leave empty for UI fallback
      author: "",           // not in schema; leave empty for UI fallback
      image: row.featured_image_url ?? null,
      featured: false       // not in schema; default false
    }));

    return successResponse(mapped);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.fetchNewsArticles'));
  }
}

export async function fetchTestimonials(): Promise<ServiceResponse<Testimonial[]>> {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    // Align to current schema: testimonials(name, content, rating, approved)
    // Only fetch approved testimonials for public display
    // Limit to reasonable number for homepage display (e.g., 6-9 testimonials)
    const { data, error } = await safeFrom("testimonials")
      .select("id, name, content, rating, approved")
      .eq("approved", true)
      .order("id", { ascending: true })
      .limit(9); // Limit to 9 testimonials max for homepage

    if (error) {
      throw error;
    }

    // Map DB shape to UI/Testimonial shape
    const mapped: Testimonial[] = (data ?? []).map((row: any) => ({
      id: row.id,
      quote: row.content ?? "",
      author: row.name ?? "",
      role: null, // role column doesn't exist in schema
      membership: null, // membership column doesn't exist in schema
    }));

    return successResponse(mapped);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.fetchTestimonials'));
  }
}

// Additional helper functions for other content types
export async function fetchSponsors(): Promise<ServiceResponse<any[]>> {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom("sponsors")
      .select("*")
      .eq("is_active", true)
      .order("sponsor_level", { ascending: false });

    if (error) {
      throw error;
    }

    return successResponse(data ?? []);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.fetchSponsors'));
  }
}

export async function fetchPartners(): Promise<ServiceResponse<any[]>> {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom("partners")
      .select("*")
      .eq("is_active", true);

    if (error) {
      throw error;
    }

    return successResponse(data ?? []);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.fetchPartners'));
  }
}

export async function fetchSlideshowSlides(): Promise<ServiceResponse<any[]>> {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom("slideshow_slides")
      .select("*")
      .eq("published", true)
      .order("order_index", { ascending: true });

    if (error) {
      throw error;
    }

    return successResponse(data ?? []);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.fetchSlideshowSlides'));
  }
}

export async function submitMembershipApplication(application: {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization?: string;
  membership_type?: string;
  membership_length?: string;
  notes?: string;
}): Promise<ServiceResponse<any>> {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error("Database not configured"), 'content.submitMembershipApplication'));
  }

  try {
    const { data, error } = await safeFrom("membership_applications")
      .insert([application])
      .select()
      .single();

    if (error) {
      throw error;
    }

    return successResponse(data);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'content.submitMembershipApplication'));
  }
}











